import React, { useEffect, useState } from 'react';
import api from '../lib/api';
import { fmt, fmtDateShort, todayISO } from '../lib/format';
import { Wallet, Plus, Check, Trash, Receipt, Trophy } from '@phosphor-icons/react';

function currentMonth() {
  return new Date().toISOString().slice(0, 7);
}

export default function Advances() {
  const [items, setItems] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);

  // Salary payment state
  const [month, setMonth] = useState(currentMonth());
  const [pending, setPending] = useState([]);
  const [paidThisMonth, setPaidThisMonth] = useState([]);
  const [payDialog, setPayDialog] = useState(null);

  const load = async () => {
    const [a, e] = await Promise.all([api.get('/salary-advances'), api.get('/employees')]);
    setItems(a.data);
    setEmployees(e.data.filter(x => x.role === 'employee' || x.role === 'manager'));
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const loadSalary = async () => {
    const [p, paid] = await Promise.all([
      api.get('/salary-payments/pending', { params: { month } }),
      api.get('/salary-payments', { params: { month } }),
    ]);
    setPending(p.data);
    setPaidThisMonth(paid.data);
  };
  useEffect(() => { loadSalary(); /* eslint-disable-next-line */ }, [month]);

  const unpaidTotal = items.filter(i => i.status === 'unpaid').reduce((s, i) => s + i.amount, 0);

  const repay = async (id) => {
    await api.put(`/salary-advances/${id}/repay`); load();
  };
  const del = async (id) => {
    if (!confirm('确认删除?')) return; // eslint-disable-line
    await api.delete(`/salary-advances/${id}`); load();
  };

  const revertPayment = async (id, name, m) => {
    if (!confirm(`确认撤销 ${name} ${m} 的工资发放?借支会恢复为未还,开销也会删除。`)) return; // eslint-disable-line
    try {
      await api.delete(`/salary-payments/${id}`);
      load();
      loadSalary();
    } catch (e) {
      alert(e.response?.data?.detail || '撤销失败');
    }
  };

  const byEmp = {};
  items.forEach(i => {
    if (!byEmp[i.employee_id]) byEmp[i.employee_id] = { name: i.employee_name, unpaid: 0, items: [] };
    byEmp[i.employee_id].items.push(i);
    if (i.status === 'unpaid') byEmp[i.employee_id].unpaid += i.amount;
  });

  return (
    <div className="space-y-5 fade-in" data-testid="advances-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-white tracking-tight">员工借支与工资</h1>
          <p className="text-sm text-slate-400 mt-0.5">借工钱记录 + 月度工资发放</p>
        </div>
        <button onClick={() => setCreating(true)} className="btn-primary text-sm inline-flex items-center gap-2" data-testid="add-advance-btn">
          <Plus size={16} /> 新增借支
        </button>
      </div>

      {/* ===================== Salary Payment Section ===================== */}
      <div className="card-base p-5 sm:p-6" data-testid="salary-payment-section">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
          <div className="flex items-center gap-2">
            <Trophy size={22} weight="duotone" className="text-amber-400" />
            <h2 className="text-lg font-semibold text-white">月度工资发放</h2>
          </div>
          <input
            type="month"
            value={month}
            onChange={e => setMonth(e.target.value)}
            className="input-base font-num text-sm py-1.5 max-w-[160px]"
            data-testid="salary-month-picker"
          />
        </div>

        {/* Pending payments */}
        {pending.length === 0 && paidThisMonth.length > 0 && (
          <div className="text-sm text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 rounded-lg p-3 mb-4">
            ✓ {month} 工资已全部发放完毕
          </div>
        )}
        {pending.length === 0 && paidThisMonth.length === 0 && (
          <div className="text-sm text-slate-500 py-4 text-center">{month} 没有需要发工资的员工</div>
        )}

        {pending.length > 0 && (
          <div className="space-y-2 mb-4">
            <div className="label-tiny">待发放 ({pending.length})</div>
            {pending.map(p => {
              const net = p.base_salary - p.advance_total;
              return (
                <div key={p.employee_id} className="p-4 rounded-lg bg-slate-900/40 border border-slate-800 hover:border-slate-700 transition-colors">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    <div>
                      <div className="text-sm font-semibold text-white">{p.employee_name}</div>
                      <div className="text-xs text-slate-500 mt-1 font-num">
                        底薪 <span className="text-white">RM {fmt(p.base_salary)}</span>
                        {p.advance_total > 0 && <> · 借支 <span className="text-amber-300">-RM {fmt(p.advance_total)}</span> ({p.advance_count} 笔)</>}
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="text-right">
                        <div className="text-[10px] uppercase text-slate-500">预估应付(未扣杂费)</div>
                        <div className="font-num text-lg font-semibold text-emerald-400">RM {fmt(net)}</div>
                      </div>
                      <button
                        onClick={() => setPayDialog(p)}
                        data-testid={`pay-${p.employee_id}`}
                        className="btn-primary text-sm py-2 px-4 inline-flex items-center gap-1.5 whitespace-nowrap"
                      >
                        <Check size={14} /> 发放工资
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Paid this month */}
        {paidThisMonth.length > 0 && (
          <div className="space-y-2">
            <div className="label-tiny">已发放 ({paidThisMonth.length})</div>
            {paidThisMonth.map(p => (
              <div key={p.id} className="p-4 rounded-lg bg-emerald-500/5 border border-emerald-500/20" data-testid={`paid-record-${p.id}`}>
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-500/30">✓ 已发放</span>
                      <span className="text-sm font-semibold text-white">{p.employee_name}</span>
                    </div>
                    <div className="text-xs text-slate-400 mt-1 font-num space-y-0.5">
                      <div>
                        底薪 RM {fmt(p.base_salary)} − 借支 RM {fmt(p.advance_deducted)} − 杂费 RM {fmt(p.other_deductions || 0)} = <span className="text-emerald-300 font-semibold">实付 RM {fmt(p.net_paid)}</span>
                      </div>
                      {p.other_deductions_notes && <div className="text-slate-500">杂费说明:{p.other_deductions_notes}</div>}
                      {p.notes && <div className="text-slate-500">备注:{p.notes}</div>}
                      <div className="text-slate-500">由 {p.paid_by} 于 {fmtDateShort(p.paid_at)} 发放</div>
                    </div>
                  </div>
                  <button
                    onClick={() => revertPayment(p.id, p.employee_name, p.month)}
                    title="撤销发放(借支会恢复)"
                    className="text-rose-400 p-1.5 rounded hover:bg-rose-500/10 self-start"
                  >
                    <Trash size={14} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ===================== Advances Section ===================== */}
      <div className="card-base p-5">
        <div className="flex items-center gap-2 mb-1">
          <Wallet size={18} weight="duotone" className="text-amber-400" />
          <span className="label-tiny">未还借支总额</span>
        </div>
        <div className="font-num text-3xl font-semibold text-amber-400" data-testid="unpaid-total">RM {fmt(unpaidTotal)}</div>
      </div>

      {loading && <div className="card-base p-12 text-center text-slate-500">加载中…</div>}

      {!loading && Object.values(byEmp).map(g => (
        <div key={g.name} className="card-base p-5">
          <div className="flex justify-between items-center mb-3">
            <h3 className="text-lg font-semibold text-white">{g.name}</h3>
            <div className="text-sm">
              未还:<span className="font-num text-amber-400 font-semibold">RM {fmt(g.unpaid)}</span>
            </div>
          </div>
          <div className="space-y-2">
            {g.items.map(i => (
              <div key={i.id} className="flex items-center justify-between p-3 rounded-lg bg-slate-900/40 border border-slate-800">
                <div>
                  <div className="font-num text-sm text-white">RM {fmt(i.amount)} <span className={`ml-2 text-xs px-2 py-0.5 rounded-full ${
                    i.status === 'unpaid' ? 'bg-amber-500/15 text-amber-300' : 'bg-emerald-500/15 text-emerald-300'
                  }`}>{i.status === 'unpaid' ? '未还' : '已还'}</span></div>
                  <div className="text-xs text-slate-500 font-num">{fmtDateShort(i.date)} {i.notes ? `· ${i.notes}` : ''}</div>
                </div>
                <div className="flex gap-2">
                  {i.status === 'unpaid' && (
                    <button onClick={() => repay(i.id)} className="btn-secondary text-xs px-3 py-1.5 inline-flex items-center gap-1" data-testid={`repay-${i.id}`}>
                      <Check size={12} /> 标记已还
                    </button>
                  )}
                  <button onClick={() => del(i.id)} className="text-rose-400 p-1.5 rounded hover:bg-rose-500/10"><Trash size={14}/></button>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}

      {!loading && items.length === 0 && (
        <div className="card-base p-12 text-center">
          <Wallet size={40} weight="duotone" className="text-slate-700 mx-auto mb-3" />
          <p className="text-slate-500">还没有借支记录</p>
        </div>
      )}

      {creating && <CreateAdvance employees={employees} onClose={() => setCreating(false)} onDone={() => { setCreating(false); load(); }} />}
      {payDialog && <PayDialog data={payDialog} month={month} onClose={() => setPayDialog(null)} onDone={() => { setPayDialog(null); load(); loadSalary(); }} />}
    </div>
  );
}

function PayDialog({ data, month, onClose, onDone }) {
  const [otherDeductions, setOtherDeductions] = useState('0');
  const [otherNotes, setOtherNotes] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const od = parseFloat(otherDeductions || 0);
  const net = data.base_salary - data.advance_total - (isNaN(od) ? 0 : od);

  const submit = async () => {
    setError('');
    setSubmitting(true);
    try {
      await api.post('/salary-payments', {
        employee_id: data.employee_id,
        month,
        other_deductions: parseFloat(otherDeductions || 0),
        other_deductions_notes: otherNotes,
        notes,
      });
      onDone();
    } catch (e) {
      setError(e.response?.data?.detail || '发放失败');
    } finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="card-base p-6 max-w-md w-full" data-testid="pay-dialog">
        <h3 className="text-lg font-semibold text-white mb-1">发放工资 · {data.employee_name}</h3>
        <p className="text-xs text-slate-500 mb-4">{month} 月工资</p>

        <div className="bg-slate-900/50 border border-slate-800 rounded-lg p-3 space-y-2 mb-4">
          <div className="flex justify-between text-sm">
            <span className="text-slate-400">底薪</span>
            <span className="font-num text-white">RM {fmt(data.base_salary)}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-slate-400">借支抵扣 ({data.advance_count} 笔)</span>
            <span className="font-num text-amber-300">- RM {fmt(data.advance_total)}</span>
          </div>
          <div className="flex justify-between text-sm items-center">
            <span className="text-slate-400">杂费扣款 (EPF / SOCSO 等)</span>
            <div className="flex items-center gap-1">
              <span className="text-amber-300 text-sm">-RM</span>
              <input
                type="number"
                step="0.01"
                min="0"
                className="input-base font-num text-right text-sm py-1 w-24"
                value={otherDeductions}
                onChange={e => setOtherDeductions(e.target.value)}
                data-testid="other-deductions"
              />
            </div>
          </div>
          <div className="h-px bg-slate-700/50" />
          <div className="flex justify-between text-base font-semibold">
            <span className="text-white">实际发放</span>
            <span className={`font-num ${net < 0 ? 'text-rose-400' : 'text-emerald-400'}`} data-testid="net-pay">RM {fmt(net)}</span>
          </div>
        </div>

        <div className="space-y-3">
          <div>
            <label className="label-tiny block mb-1">杂费说明</label>
            <input
              className="input-base text-sm"
              placeholder="EPF 11% + SOCSO 1%"
              value={otherNotes}
              onChange={e => setOtherNotes(e.target.value)}
              data-testid="other-deductions-notes"
            />
          </div>
          <div>
            <label className="label-tiny block mb-1">备注 (可选)</label>
            <input
              className="input-base text-sm"
              placeholder="例如:6月3日转账发放"
              value={notes}
              onChange={e => setNotes(e.target.value)}
            />
          </div>
        </div>

        {error && (
          <div className="mt-3 text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2">
            {error}
          </div>
        )}

        <div className="flex gap-3 mt-5">
          <button onClick={onClose} className="btn-secondary flex-1">取消</button>
          <button
            onClick={submit}
            disabled={submitting || net < 0}
            className="btn-primary flex-1 inline-flex items-center justify-center gap-1.5 disabled:opacity-50"
            data-testid="confirm-pay"
          >
            <Check size={14} /> {submitting ? '处理中…' : `确认发放 RM ${fmt(net)}`}
          </button>
        </div>
        <p className="text-[10px] text-slate-500 mt-3">
          ※ 确认后:借支会自动标记为已还 + 「开销管理」自动加一笔 RM {fmt(data.base_salary)} 工资开销
        </p>
      </div>
    </div>
  );
}

function CreateAdvance({ employees, onClose, onDone }) {
  const [empId, setEmpId] = useState(employees[0]?.id || '');
  const [date, setDate] = useState(todayISO());
  const [amount, setAmount] = useState('');
  const [notes, setNotes] = useState('');

  const submit = async () => {
    await api.post('/salary-advances', {
      employee_id: empId, date, amount: parseFloat(amount), notes,
    });
    onDone();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="card-base p-6 max-w-md w-full">
        <h3 className="text-lg font-semibold text-white mb-4">新增借支</h3>
        <div className="space-y-3">
          <div>
            <label className="label-tiny block mb-1">员工</label>
            <select className="input-base" value={empId} onChange={e => setEmpId(e.target.value)} data-testid="adv-emp">
              {employees.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
            </select>
          </div>
          <div>
            <label className="label-tiny block mb-1">日期</label>
            <input type="date" className="input-base font-num" value={date} onChange={e => setDate(e.target.value)} />
          </div>
          <div>
            <label className="label-tiny block mb-1">金额 (MYR)</label>
            <input type="number" step="0.01" className="input-base font-num" value={amount} onChange={e => setAmount(e.target.value)} data-testid="adv-amount" />
          </div>
          <div>
            <label className="label-tiny block mb-1">备注</label>
            <input className="input-base" value={notes} onChange={e => setNotes(e.target.value)} placeholder="家里急用" />
          </div>
          <div className="flex gap-3 pt-2">
            <button onClick={onClose} className="btn-secondary flex-1">取消</button>
            <button onClick={submit} className="btn-primary flex-1" data-testid="save-advance">保存</button>
          </div>
        </div>
      </div>
    </div>
  );
}
